/**
 * 🚨 EMERGENCY PAGE
 * Life-saving exit guidance with compass
 */

import { useState, useEffect } from 'react';
import { ArrowUp, Users } from 'lucide-react';
import styles from './Emergency.module.css';

export function EmergencyPage() {
  const [heading, setHeading] = useState(0);
  const [distance, setDistance] = useState(28);
  const [exitName, setExitName] = useState('Exit 4');

  useEffect(() => {
    // Request compass permission
    if ('DeviceOrientationEvent' in window) {
      const handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.alpha !== null) {
          setHeading(event.alpha);
        }
      };

      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        (DeviceOrientationEvent as any).requestPermission()
          .then((response: string) => {
            if (response === 'granted') {
              window.addEventListener('deviceorientation', handleOrientation);
            }
          });
      } else {
        window.addEventListener('deviceorientation', handleOrientation);
      }

      return () => {
        window.removeEventListener('deviceorientation', handleOrientation);
      };
    }
  }, []);

  // Simulate distance countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setDistance((d) => Math.max(0, d - 2));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const getDirection = () => {
    const angle = (45 - heading + 360) % 360;
    if (angle < 45 || angle >= 315) return 'AHEAD';
    if (angle < 135) return 'AHEAD-RIGHT';
    if (angle < 225) return 'BEHIND';
    return 'AHEAD-LEFT';
  };

  return (
    <div className={styles.container}>
      {/* Emergency Banner */}
      <div className={styles.banner}>
        <span className={styles.bannerIcon}>⚠️</span>
        <span className={styles.bannerText}>EMERGENCY MODE ACTIVE</span>
      </div>

      {/* Main Compass Area */}
      <div className={styles.compassContainer}>
        <div 
          className={styles.arrow}
          style={{ transform: `rotate(${45 - heading}deg)` }}
        >
          <ArrowUp size={150} strokeWidth={3} />
        </div>

        <div className={styles.exitInfo}>
          <span className={styles.exitLabel}>YOUR NEAREST EXIT:</span>
          <span className={styles.exitName}>{exitName}</span>
        </div>

        <div className={styles.distanceInfo}>
          <span className={styles.distance}>{distance}</span>
          <span className={styles.distanceUnit}>meters</span>
        </div>

        <span className={styles.direction}>{getDirection()}</span>
      </div>

      {/* Instructions */}
      <div className={styles.instructions}>
        <h2 className={styles.instructionsTitle}>STAY CALM</h2>
        <p className={styles.instructionsText}>Follow the arrow</p>
        <p className={styles.instructionsText}>Walk, don't run</p>
        <p className={styles.instructionsText}>Follow venue staff instructions</p>
      </div>

      {/* Family Beacon */}
      <div className={styles.familyBeacon}>
        <Users size={20} />
        <span>Family Beacon: Active</span>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <p>This is not a drill.</p>
        <p>Follow venue staff instructions.</p>
      </div>
    </div>
  );
}
